import * as React from "react";
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Link,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import { useAccount } from "./accountContext.jsx";
import { requestPos } from "./posApi";
import { TRIAL_PERIOD_DAYS } from "../data/billzappPlans";

// The server's rules (routes/account.js in the POS backend), checked here too so mistakes
// show beside the field before anything is sent.
const normalisePhone = (value) => value.replace(/[\s-]/g, "").replace(/^(\+91|91|0)(?=\d{10}$)/, "");

function validateSignup(form) {
  const errors = {};
  if (!form.businessName.trim()) errors.businessName = "Enter your business name.";
  if (!form.address.trim()) errors.address = "Enter your business address.";
  if (!/^[6-9]\d{9}$/.test(normalisePhone(form.phone))) errors.phone = "Enter a 10-digit mobile number.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = "Enter a valid email address.";
  if (!/^[A-Za-z0-9._-]{3,30}$/.test(form.username.trim())) errors.username = "3 to 30 letters, numbers, dots, dashes or underscores.";
  if (form.password.length < 8) errors.password = "At least 8 characters.";
  if (form.confirmPassword !== form.password) errors.confirmPassword = "The passwords don't match.";
  return errors;
}

const SIGNUP_FIELDS = [
  { key: "businessName", label: "Business name", autoComplete: "organization", sm: 12 },
  { key: "address", label: "Business address", autoComplete: "street-address", sm: 12 },
  { key: "phone", label: "Mobile number", autoComplete: "tel", type: "tel", sm: 6 },
  { key: "email", label: "Email", autoComplete: "email", type: "email", sm: 6 },
  { key: "username", label: "Username", autoComplete: "username", sm: 12, helper: "You log in to BillZapp with this." },
  { key: "password", label: "Password", autoComplete: "new-password", type: "password", sm: 6 },
  { key: "confirmPassword", label: "Confirm password", autoComplete: "new-password", type: "password", sm: 6 },
];

const EMPTY_SIGNUP = { businessName: "", address: "", phone: "", email: "", username: "", password: "", confirmPassword: "", planId: 1 };

// Asks for the 6-digit code the server emailed (sign-up, email change or password reset).
// `onVerify(code)` and `onResend()` call the server; `onResend` resolves with { resendInSeconds }.
// `children` are extra fields shown under the code (the new password, for a reset).
export function CodeEntry({
  email, codeMinutes, resendInSeconds, onVerify, onResend, onBack, backLabel, verifyLabel,
  instructions = "Enter it here to confirm the address.", children,
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [notice, setNotice] = useState("");
  const [wait, setWait] = useState(resendInSeconds || 0);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const timer = setTimeout(() => setWait(wait - 1), 1000);
    return () => clearTimeout(timer);
  }, [wait]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setMessage("Enter the 6-digit code from the email.");
      return;
    }
    setBusy(true);
    setMessage("");
    setNotice("");
    try {
      await onVerify(code);
    } catch (err) {
      setMessage(err.message);
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setMessage("");
    try {
      const { resendInSeconds: nextWait } = await onResend();
      setWait(nextWait);
      setCode("");
      setNotice(`We sent a new code to ${email}.`);
    } catch (err) {
      setMessage(err.message);
    }
  };

  return (
    <Box component="form" noValidate onSubmit={handleSubmit}>
      <Typography sx={{ mb: 2 }}>
        We sent a 6-digit code to <b>{email}</b>. {instructions} It works for {codeMinutes || 10} minutes.
      </Typography>
      {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
      <TextField
        label="Verification code"
        value={code}
        onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
        inputProps={{ inputMode: "numeric", autoComplete: "one-time-code", maxLength: 6, style: { letterSpacing: 8, fontSize: 22 } }}
        disabled={busy}
        autoFocus
        fullWidth
        sx={{ mb: 2 }}
      />
      {children}
      <Button type="submit" variant="contained" size="large" fullWidth disabled={busy}>
        {busy ? <CircularProgress size={26} color="inherit" /> : verifyLabel}
      </Button>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
        <Button onClick={onBack} disabled={busy}>{backLabel}</Button>
        <Button onClick={handleResend} disabled={busy || wait > 0}>
          {wait > 0 ? `Resend code in ${wait}s` : "Resend code"}
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Can't find it? Check your spam or promotions folder.
      </Typography>
    </Box>
  );
}

// `notice` is shown above the form, e.g. after a password reset (`initialIdentifier` then holds
// the account's username).
function LoginForm({ onDone, onForgot, initialIdentifier = "", notice }) {
  const { signIn } = useAccount();
  const [identifier, setIdentifier] = useState(initialIdentifier);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!identifier.trim() || !password) {
      setMessage("Enter your username or email and your password.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      signIn(await requestPos("/account/login", { method: "POST", body: { identifier, password } }));
      onDone?.();
    } catch (err) {
      setMessage(err.message);
      setBusy(false);
    }
  };

  return (
    <Box component="form" noValidate onSubmit={handleSubmit}>
      {notice && !message && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
      {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}
      <TextField
        label="Username or email"
        value={identifier}
        onChange={(event) => setIdentifier(event.target.value)}
        autoComplete="username"
        disabled={busy}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Password"
        type="password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        autoComplete="current-password"
        disabled={busy}
        autoFocus={Boolean(initialIdentifier)}
        fullWidth
        sx={{ mb: 1 }}
      />
      <Box sx={{ textAlign: "right", mb: 1 }}>
        <Link component="button" type="button" variant="body2" onClick={() => onForgot?.(identifier)} disabled={busy}>
          Forgot password?
        </Link>
      </Box>
      <Button type="submit" variant="contained" size="large" fullWidth disabled={busy}>
        {busy ? <CircularProgress size={26} color="inherit" /> : "Log in"}
      </Button>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        Use your BillZapp owner login.
      </Typography>
    </Box>
  );
}

// Forgotten password: the username or email first, then the emailed code with the new password.
// `onDone(username)` runs once the password has been changed.
function ForgotPasswordForm({ initialIdentifier = "", onBack, onDone }) {
  const [identifier, setIdentifier] = useState(initialIdentifier);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [pending, setPending] = useState(null); // { ref, email, codeMinutes, resendInSeconds } once the code is sent

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!identifier.trim()) {
      setMessage("Enter your username or email.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      setPending(await requestPos("/account/password/forgot", { method: "POST", body: { identifier } }));
    } catch (err) {
      setMessage(err.message);
    }
    setBusy(false);
  };

  if (pending) {
    return (
      <CodeEntry
        email={pending.email}
        codeMinutes={pending.codeMinutes}
        resendInSeconds={pending.resendInSeconds}
        instructions="Enter it below with your new password."
        verifyLabel="Set new password"
        backLabel="Back"
        onBack={() => setPending(null)}
        onResend={() => requestPos("/account/code/resend", { method: "POST", body: { ref: pending.ref } })}
        onVerify={async (code) => {
          if (password.length < 8) throw new Error("Choose a new password of at least 8 characters.");
          if (confirmPassword !== password) throw new Error("The new passwords don't match.");
          const { username } = await requestPos("/account/password/reset", {
            method: "POST",
            body: { ref: pending.ref, code, password },
          });
          onDone(username);
        }}
      >
        <TextField
          label="New password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          helperText="At least 8 characters."
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label="Confirm new password"
          type="password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          autoComplete="new-password"
          fullWidth
          sx={{ mb: 2 }}
        />
      </CodeEntry>
    );
  }

  return (
    <Box component="form" noValidate onSubmit={handleSubmit}>
      <Typography sx={{ mb: 2 }}>
        Enter your BillZapp username or email. We will email a code to the address on your account.
      </Typography>
      {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}
      <TextField
        label="Username or email"
        value={identifier}
        onChange={(event) => setIdentifier(event.target.value)}
        autoComplete="username"
        disabled={busy}
        autoFocus
        fullWidth
        sx={{ mb: 2 }}
      />
      <Button type="submit" variant="contained" size="large" fullWidth disabled={busy}>
        {busy ? <CircularProgress size={26} color="inherit" /> : "Send code"}
      </Button>
      <Button onClick={onBack} disabled={busy} fullWidth sx={{ mt: 1 }}>
        Back to log in
      </Button>
    </Box>
  );
}

// `planId` starts the free trial on the plan of the product being bought; otherwise it starts
// on Basic, as in the BillZapp app. The server emails a code before it creates the account.
function SignupForm({ planId, onDone }) {
  const { signIn } = useAccount();
  const [form, setForm] = useState({ ...EMPTY_SIGNUP, planId: planId || 1 });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [pending, setPending] = useState(null); // { ref, email, codeMinutes, resendInSeconds } once the code is sent

  const updateField = (key) => (event) => {
    setForm({ ...form, [key]: event.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const fieldErrors = validateSignup(form);
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    setBusy(true);
    setMessage("");
    try {
      const { confirmPassword, ...body } = form;
      setPending(await requestPos("/account/signup", { method: "POST", body }));
    } catch (err) {
      setMessage(err.message);
    }
    setBusy(false);
  };

  if (pending) {
    return (
      <CodeEntry
        // Not {...pending}: its `ref` field would be taken as a React ref.
        email={pending.email}
        codeMinutes={pending.codeMinutes}
        resendInSeconds={pending.resendInSeconds}
        verifyLabel="Verify and create account"
        backLabel="Change details"
        onBack={() => setPending(null)}
        onResend={() => requestPos("/account/code/resend", { method: "POST", body: { ref: pending.ref } })}
        onVerify={async (code) => {
          // The password is sent again here; the server doesn't keep it while the code is out.
          signIn(await requestPos("/account/signup/verify", {
            method: "POST",
            body: { ref: pending.ref, code, password: form.password },
          }));
          onDone?.();
        }}
      />
    );
  }

  return (
    <Box component="form" noValidate onSubmit={handleSubmit}>
      {message && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}
      <Grid container spacing={2}>
        {SIGNUP_FIELDS.map((field) => (
          <Grid item xs={12} sm={field.sm} key={field.key}>
            <TextField
              label={field.label}
              value={form[field.key]}
              onChange={updateField(field.key)}
              error={Boolean(errors[field.key])}
              helperText={errors[field.key] || field.helper}
              type={field.type || "text"}
              autoComplete={field.autoComplete}
              disabled={busy}
              fullWidth
            />
          </Grid>
        ))}
      </Grid>
      <Button type="submit" variant="contained" size="large" fullWidth disabled={busy} sx={{ mt: 2 }}>
        {busy ? <CircularProgress size={26} color="inherit" /> : "Continue"}
      </Button>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        Next we email you a code to confirm your address. This is your BillZapp account: it starts
        with a {TRIAL_PERIOD_DAYS}-day free trial, and you use the same login in the BillZapp app.
      </Typography>
    </Box>
  );
}

// Log in or create a BillZapp account. `onDone` runs once the visitor is logged in.
export default function AccountForms({ planId, onDone, initialTab = "login" }) {
  const [tab, setTab] = useState(initialTab);
  // The log-in tab shows the forgotten-password form while `forgot` holds what was typed so far.
  const [forgot, setForgot] = useState(null);
  const [reset, setReset] = useState(null); // { username } after a password reset

  const loginTab = forgot !== null ? (
    <ForgotPasswordForm
      initialIdentifier={forgot}
      onBack={() => setForgot(null)}
      onDone={(username) => {
        setForgot(null);
        setReset({ username });
      }}
    />
  ) : (
    <LoginForm
      // A fresh form after a reset, starting with the account's username.
      key={reset ? reset.username : "login"}
      onDone={onDone}
      onForgot={(identifier) => setForgot(identifier)}
      initialIdentifier={reset?.username}
      notice={reset && "Your password has been changed. Log in with your new password."}
    />
  );

  return (
    <Box>
      <Tabs value={tab} onChange={(event, value) => setTab(value)} variant="fullWidth" sx={{ mb: 3 }}>
        <Tab value="login" label="Log in" />
        <Tab value="signup" label="Create account" />
      </Tabs>
      {tab === "login" ? loginTab : <SignupForm planId={planId} onDone={onDone} />}
    </Box>
  );
}
